import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import parse from 'html-react-parser';
import utilsForms from '../../_Utils/utilsForms';
import FormFieldRender from './FormFieldRender';
import './FormPage.css';

// עמוד ציבורי למילוי טופס/שאלון. נטען בקישור לא-ניחושי /f/:slug, ללא shell ה-Master.
// noindex + לא ב-sitemap + Disallow ב-robots.
const FormPage = () => {
    const { slug } = useParams();

    const [status, setStatus] = useState('loading'); // loading | active | closed | notfound | done | error
    const [form, setForm] = useState(null);
    const [clientName, setClientName] = useState('');
    const [answers, setAnswers] = useState({});
    const [errMsg, setErrMsg] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await utilsForms.getBySlug(slug);
                if (!alive) return;
                const data = res && res.data;
                if (!data) { setStatus('notfound'); return; }
                if (data.closed) { setForm({ title: data.title, logo: data.logo }); setStatus('closed'); return; }
                setForm(data);
                setStatus('active');
            } catch (err) {
                if (!alive) return;
                // 404 מהשרת מגיע כ-throw דרך axios
                if (err && err.response && err.response.status === 404) setStatus('notfound');
                else setStatus('error');
            }
        })();
        return () => { alive = false; };
    }, [slug]);

    const onFieldChange = (fId, val) => {
        setAnswers(prev => ({ ...prev, [fId]: val }));
    };

    const validate = () => {
        if (clientName.trim().length < 2) return 'יש להזין שם עסק / לקוח';
        for (const f of form.fields) {
            if (!f.required) continue;
            const v = answers[f.fId];
            const empty =
                f.type === 'checkbox' ? !(Array.isArray(v) && v.length) :
                    f.type === 'yesno' ? (v !== true && v !== false) :
                        f.type === 'number' ? (v === '' || v == null) :
                            !v || String(v).trim() === '';
            if (empty) return `חסרה תשובה לשדה חובה: ${f.label}`;
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const v = validate();
        if (v) { setErrMsg(v); return; }
        setErrMsg('');
        setSending(true);
        try {
            const res = await utilsForms.submit(slug, { clientName: clientName.trim(), answers });
            if (res && res.ok) setStatus('done');
            else setErrMsg((res && res.errMsg) || 'שגיאה בשליחת הטופס');
        } catch (err) {
            setErrMsg('שגיאה בשליחת הטופס');
        } finally {
            setSending(false);
        }
    };

    const noIndex = (
        <Helmet>
            <meta name="robots" content="noindex, nofollow" />
            <title>{form && form.title ? form.title : 'טופס'}</title>
        </Helmet>
    );

    // ראש העמוד: לוגו אשדוד בתפריט צמוד לימין, כותרת ממורכזת בשורה נפרדת מתחתיו.
    const headerRow = (title) => (
        <div className="formPageHead">
            {form && form.logo && (
                <img className="formPageLogo" src={form.logo} alt="אשדוד בתפריט" />
            )}
            <h1 className="formPageTitle">{title}</h1>
        </div>
    );

    if (status === 'loading') {
        return <div className="formPageWrap">{noIndex}<div className="formPageCard formPageMsg">טוען…</div></div>;
    }

    if (status === 'notfound') {
        return <div className="formPageWrap">{noIndex}<div className="formPageCard formPageMsg">הטופס לא נמצא.</div></div>;
    }

    if (status === 'error') {
        return <div className="formPageWrap">{noIndex}<div className="formPageCard formPageMsg">אירעה שגיאה בטעינת הטופס. נסו שוב מאוחר יותר.</div></div>;
    }

    if (status === 'closed') {
        return (
            <div className="formPageWrap">
                {noIndex}
                <div className="formPageCard formPageMsg">
                    {headerRow(form.title)}
                    <p>הטופס סגור כרגע ואינו מקבל תשובות.</p>
                </div>
            </div>
        );
    }

    if (status === 'done') {
        const thankTitle = (form && form.thankTitle && form.thankTitle.trim()) || 'תודה!';
        const thankBody = (form && form.thankBody && form.thankBody.trim()) || 'התשובות נשלחו בהצלחה.';
        return (
            <div className="formPageWrap">
                {noIndex}
                <div className="formPageCard formPageMsg">
                    {headerRow(thankTitle)}
                    <div className="formPageThankBody">{parse(String(thankBody))}</div>
                </div>
            </div>
        );
    }

    // active
    return (
        <div className="formPageWrap">
            {noIndex}
            <form className="formPageCard" onSubmit={handleSubmit}>
                {headerRow(form.title)}
                {form.description ? (
                    <div className="formPageDesc">{parse(String(form.description))}</div>
                ) : null}

                {/* שדה קבוע ראשון - שם העסק / לקוח */}
                <div className="formFieldWrap">
                    <label className="formFieldLabel" htmlFor="clientName">
                        שם העסק / לקוח<span className="formFieldReq"> *</span>
                    </label>
                    <input
                        id="clientName"
                        type="text"
                        className="formFieldInput"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                    />
                </div>

                {form.fields.map((field) => (
                    <FormFieldRender
                        key={field.fId}
                        field={field}
                        value={answers[field.fId]}
                        onChange={onFieldChange}
                    />
                ))}

                {errMsg ? <div className="formPageErr">{errMsg}</div> : null}

                <button type="submit" className="formPageSubmit" disabled={sending}>
                    {sending ? 'שולח…' : 'שליחה'}
                </button>
            </form>
        </div>
    );
};

export default FormPage;
